/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, shareReplay, tap } from 'rxjs/operators';
import { ConfigService } from '@xilo-mono/common/config';
import { BehaviorSubject, Observable } from 'rxjs';
import { Agent, IPermissions } from '../models/agent.model';
import { AgentGetResponse } from '../models/agent-get-response.model';

export interface AgentResponse {
  email: string;
  firstName: string;
  id: number;
  lastName: string;
  createdAt: string;
}

@Injectable({
  providedIn: 'root'
})
export class AgentService {
  NEW_CLIENT_URL = `${this.configService.apiUrl}/new/client`;
  AGENT_URL = `${this.configService.apiUrl}/agent`;
  agents = new BehaviorSubject<Agent[]>([]);
  agents$ = this.agents.asObservable();
  private agentSubject = new BehaviorSubject<AgentGetResponse | null>(null);
  public agent$ = this.agentSubject.asObservable();

  public get agent() {
    return this.agentSubject.getValue();
  }

  constructor(private http: HttpClient, private configService: ConfigService) {}

  public get() {
    return this.http.get<AgentGetResponse>(`${this.AGENT_URL}/self/list-one`).pipe(
      tap(result => {
        this.agentSubject.next(result);
      })
    );
  }

  getAgentById(id: number, agentOnly = false): Observable<{ obj: Agent }> {
    return this.http.get<{ obj: Agent }>(`${this.AGENT_URL}/${id}?agentOnly=${agentOnly}`);
  }

  getAgentIdByEmail(agent: string) {
    const body = { agent: agent };
    return this.http.post(`${this.NEW_CLIENT_URL}/agent/email`, body).pipe(map(result => result['id']));
  }

  getAgentEmailById(id: number) {
    return this.http.get(`${this.NEW_CLIENT_URL}/agent/list-one/${id}`).pipe(map(result => result['obj']));
  }

  getAgentByRoundRobin(formId: any) {
    return this.http.get(`${this.NEW_CLIENT_URL}/agent/round-robin/${formId}`).pipe(map(result => result['obj']));
  }

  getAgents() {
    return this.http.get(`${this.AGENT_URL}`).pipe(
      map((result: { title: string; obj: Agent[] }) => {
        if (result?.obj) {
          result.obj = result.obj.sort((a, b) => a.firstName.localeCompare(b.firstName));
        }
        this.agents.next(result.obj);
        return result.obj;
      }),
      shareReplay(1)
    );
  }

  updateOne(agent: Agent) {
    return this.http.patch<AgentGetResponse>(`${this.AGENT_URL}/${agent.id}`, agent).pipe(
      tap(() => {
        this.agentSubject.next({...this.agent, obj: agent});
      }),
      map((result: AgentGetResponse) => {
        return result.obj;
      })
    );
  }

  updatePermissions(agentId: number, permissions: IPermissions): Observable<any> {
    return this.http.patch(`${this.AGENT_URL}/${agentId}/permissions`, permissions);
  }

  getAgentsByCompany(companyId: any) {
    return this.http.get(`${this.NEW_CLIENT_URL}/company/${companyId}/agents`).pipe(map(result => result['obj']));
  }
}
